import React, { ReactNode } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import {
	UpdateSocialSignupInitialValues,
	updateSocialSignupSchema,
} from 'constants/schemaValidations';
import { SignupInterface } from 'constants/interfaces';
import { useCountryListQuery, useUpdateSocialLoginMutation } from 'services';
import ReactSelect from 'react-select';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import useTranslate from 'hooks/useTranslate';
import { getBrowserID, getPhoneFormat } from 'utils';
import toast from 'react-hot-toast';
import { AuthLeftPanel } from 'components/Auth';
import { setPreviewsRole, setUserInfo } from 'store/auth';
import AuthHeadingPanel from 'components/Auth/Common/AuthHeadingPanel';

const SeekerSignUp: React.FC = () => {
	const { state } = useLocation();
	const { firstName, lastName, email } = state?.data?.data?.user || {};
	const socialData = state?.data?.data?.socialData;
	const { translate } = useTranslate();
	const navigate = useNavigate();

	// mutation
	const { mutateAsync: updateSocialDetailsMutation } = useUpdateSocialLoginMutation();
	// query
	const { data: countryList = [] } = useCountryListQuery();

	// Check if this is a social login with missing email
	const isSocialLoginWithMissingEmail = socialData?.requiresEmail;
	const socialProvider = socialData?.provider;

	const formik: any = useFormik({
		initialValues: {
			...UpdateSocialSignupInitialValues,
			firstName: firstName || socialData?.userData?.user?.displayName?.split(' ')[0] || '',
			lastName:
				lastName || socialData?.userData?.user?.displayName?.split(' ').slice(1).join(' ') || '',
			email: email || '',
		},
		validationSchema: updateSocialSignupSchema(translate),
		onSubmit: async (values) => {
			const payload = {
				socialType: socialProvider || 'linkedin',
				socialId:
					socialData?.userData?.user?.uid || socialData?.userData?._tokenResponse?.localId || '',
				udid:
					socialData?.userData?.user?.uid || socialData?.userData?._tokenResponse?.localId || '',
				firstName: values.firstName,
				lastName: values.lastName,
				email: values.email,
				phoneNumber: values?.phoneNumber,
				countryCode: values?.countryCode,
				address: values?.address,
				countryId: values?.countryId,
				stateId: values?.stateId,
				cityId: values?.cityId,
				userType: 'seeker',
				platform: 'web',
				deviceToken: getBrowserID(),
				voipDeviceToken: '',
				token: state?.data?.data?.token,
			};
			updateSocialDetailsMutation(payload).then((result: any) => {
				if (result?.success) {
					const userData = result?.data || {};
					if (userData?.user?.isProfileCompleted) {
						setPreviewsRole(result?.data?.user?.userType);
						setUserInfo({ token: state?.data?.data?.token, user: result?.data?.user || {} });
					} else {
						navigate('/login');
					}
				} else {
					toast.error(result?.message);
					navigate('/login');
				}
			});
		},
	});

	const renderError = <T extends keyof SignupInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const handleChange = (e: any) => {
		const inputValue = getPhoneFormat(e.target.value);
		if (inputValue) {
			formik.setFieldValue('phoneNumber', e.target.value);
		}
	};

	return (
		<div className="auth bg-white d-flex flex-column flex-lg-row">
			<div className="auth-left-content">
				<AuthLeftPanel />
			</div>
			<div className="auth-right-content flex-1 d-flex align-items-center justify-content-center position-relative">
				<div className="auth-form mb-0 mt-5 mx-3 my-5 m-md-5">
					<AuthHeadingPanel description={translate('Auth.updateYourDetails')} />
					<div className="auth-form-fields">
						<Form className="form" onSubmit={formik.handleSubmit}>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.firstName')}*</Form.Label>
								<Form.Control
									disabled={!isSocialLoginWithMissingEmail}
									type="text"
									placeholder={translate('PlaceHolder.firstName')}
									name="firstName"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.firstName}
									autoComplete="off"
								/>
								{renderError('firstName')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.lastName')}*</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.lastName')}
									name="lastName"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.lastName}
									autoComplete="off"
								/>
								{renderError('lastName')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.email')}*</Form.Label>
								{isSocialLoginWithMissingEmail && (
									<div className="alert alert-info mb-2" role="alert">
										<small>
											{translate('Auth.emailPermissionRequired', { provider: socialProvider })}
										</small>
									</div>
								)}
								<Form.Control
									disabled={email && !isSocialLoginWithMissingEmail}
									type="email"
									placeholder={translate('PlaceHolder.emailText')}
									name="email"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.email}
									autoComplete="off"
								/>
								{renderError('email')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.phone')}</Form.Label>
								<div className="number-input d-flex">
									<ReactSelect
										className="form-react-select"
										classNamePrefix="select"
										isSearchable
										name="countryCode"
										autoFocus={false}
										options={countryList}
										getOptionLabel={(option: any) => option?.phonecode}
										getOptionValue={(option: any) => option?.phonecode}
										onChange={(value: any) => {
											formik.setFieldValue('countryCode', value?.phonecode);
										}}
										value={countryList?.find(
											(item: any) => item.phonecode === formik.values.countryCode,
										)}
										onBlur={formik.handleBlur}
									/>
									<Form.Control
										type="text"
										placeholder={translate('PlaceHolder.phoneText')}
										name="phoneNumber"
										className="rounded-pill flex-1"
										onChange={handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.phoneNumber}
										autoComplete="off"
										maxLength={12}
									/>
								</div>
								{renderError('countryCode') || renderError('phoneNumber')}
							</Form.Group>

							<Form.Group className="mb-4 position-relative custom-search-address">
								<Form.Label className="lh-1">{translate('PlaceHolder.location')}</Form.Label>
								<CustomSearchAddress
									selectProps={{
										placeholder: translate('PlaceHolder.locationText'),
										onChange: (value: any) => {
											formik.setFieldValue('address', value.location);
											formik.setFieldValue('cityId', value.cityId);
											formik.setFieldValue('stateId', value.stateId);
											formik.setFieldValue('countryId', value.countryId);
										},
										name: 'address',
									}}
									fieldValue={formik.values.address}
									onBlur={formik.handleBlur}
								/>
								<i className="ri-search-line form-icon position-absolute lh-1"></i>
								{renderError('address') ||
									renderError('cityId') ||
									renderError('stateId') ||
									renderError('countryId')}
							</Form.Group>

							<div className="form-btn">
								<Button type="submit" className="filled-btn rounded-pill w-100">
									{translate('Common.registerNow')}
								</Button>
							</div>
						</Form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SeekerSignUp;
